import React, { useEffect, useState, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import {
	Box,
	Button,
	Grid,
	GridItem,
	HStack,
	Image,
	Text,
	Table,
	TableContainer,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Spinner,
	useColorModeValue,
	VStack,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Avatar,
	Tooltip,
} from '@chakra-ui/react';
import { FaSyncAlt, FaChevronDown, FaAndroid, FaApple, FaQuestion } from 'react-icons/fa';
import config from '../config';
import globalStore from '../store';
import axios from 'axios';
import { Application, CodemagicBuild, CodemagicBuilds } from '@repo/alictus-common/types/build';
import CancelBuildModal from './cancel_build_modal';
import dayjs from 'dayjs';

const BuildsPage = () => {
	const [builds, setBuilds] = useState<CodemagicBuild[]>([]);
	const [applications, setApplications] = useState<Application[]>([]);
	const [selectedAppId, setSelectedAppId] = useState<string>('all');
	const [loading, setLoading] = useState(false);
	const [loadingApps, setLoadingApps] = useState(false);
	const [nextRefreshIn, setNextRefreshIn] = useState(60);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [selectedBuild, setSelectedBuild] = useState<CodemagicBuild | null>(null);
	const borderColor = useColorModeValue('gray.300', 'gray.600');

	let globalData = globalStore();
	let isAdminOrDeveloper = globalData.user?.roles?.some((role) => ['ADMIN', 'DEVELOPER'].includes(role)) || false;

	const fetchApplications = useCallback(async () => {
		setLoadingApps(true);
		try {
			let bearerToken = 'Bearer ' + globalData.JWTToken;
			let url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/codemagic_applications';
			const response = await axios.get(url, { headers: { Authorization: bearerToken } });

			const data = response.data.applications;
			if (data) {
				setApplications(data);
			} else {
				console.error('Applications field is missing in the response');
			}
		} catch (error) {
			console.error('Error fetching applications:', error);
		} finally {
			setLoadingApps(false);
		}
	}, [globalData.JWTToken, globalData.gameId]);

	const fetchBuilds = useCallback(
		async (appId?: string) => {
			setLoading(true);
			try {
				let bearerToken = 'Bearer ' + globalData.JWTToken;
				let url;

				if (appId && appId !== 'all') {
					// Fetch builds for specific application
					url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/codemagic_builds/' + appId;
				} else {
					// Fetch all builds
					url = config.CLOUDFLARE_LOCAL_ENDPOINT + '/game/' + globalData.gameId + '/codemagic_builds';
				}

				const response = await axios.get(url, { headers: { Authorization: bearerToken } });

				const data: CodemagicBuilds = response.data.build_list;
				if (data) {
					setBuilds(data);
				} else {
					console.error('Builds field is missing in the response');
					setBuilds([]);
				}
			} catch (error) {
				console.error('Error fetching builds:', error);
				setBuilds([]);
			} finally {
				setLoading(false);
			}
		},
		[globalData.JWTToken, globalData.gameId],
	);

	const handleAppSelection = (appId: string) => {
		setSelectedAppId(appId);
		fetchBuilds(appId);
	};

	const handleRefresh = () => {
		fetchBuilds(selectedAppId);
	};

	useEffect(() => {
		fetchApplications();
		fetchBuilds();
	}, [fetchApplications, fetchBuilds]);

	useEffect(() => {
		const interval = setInterval(() => {
			fetchBuilds(selectedAppId);
			setNextRefreshIn(60);
		}, 60000);

		return () => clearInterval(interval);
	}, [selectedAppId, fetchBuilds]);

	useEffect(() => {
		const countdown = setInterval(() => {
			setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
		}, 1000);

		return () => clearInterval(countdown);
	}, []);

	const runningOrPendingBuilds = builds.filter(
		(build) =>
			build.status === 'building' ||
			build.status === 'finishing' ||
			build.status === 'queued' ||
			build.status === 'fetching' ||
			build.status === 'preparing' ||
			build.status === 'publishing',
	);
	const completedBuilds = builds.filter((build) => build.status === 'finished' || build.status === 'failed' || build.status === 'canceled');

	return (
		<Box padding={4}>
			{/* Application Filter and Controls */}
			<VStack spacing={4} mb={6}>
				<HStack justify="space-between" width="100%">
					<HStack spacing={4}>
						<Text fontSize="lg" fontWeight="bold">
							Application Filter:
						</Text>
						<Menu>
							<MenuButton as={Button} rightIcon={<FaChevronDown />} width="300px" textAlign="left" isDisabled={loadingApps}>
								<HStack spacing={3}>
									{selectedAppId !== 'all' && applications.length > 0 && (
										<Avatar
											size="sm"
											src={applications.find((app) => app._id === selectedAppId)?.iconUrl}
											name={applications.find((app) => app._id === selectedAppId)?.appName}
										/>
									)}
									<Text>
										{selectedAppId === 'all'
											? 'All Applications'
											: applications.find((app) => app._id === selectedAppId)?.appName || 'Select Application'}
									</Text>
								</HStack>
							</MenuButton>
							<MenuList maxH="300px" overflowY="auto">
								<MenuItem onClick={() => handleAppSelection('all')}>
									<HStack spacing={3}>
										<Avatar size="sm" name="All" />
										<Text>All Applications</Text>
									</HStack>
								</MenuItem>
								{applications.map((app) => (
									<MenuItem key={app._id} onClick={() => handleAppSelection(app._id)}>
										<HStack spacing={3}>
											<Avatar size="sm" src={app.iconUrl} name={app.appName} />
											<Text>{app.appName}</Text>
										</HStack>
									</MenuItem>
								))}
							</MenuList>
						</Menu>
						{loadingApps && <Spinner size="sm" />}
					</HStack>

					<HStack spacing={8}>
						<Text fontSize="sm" color="gray.500">
							Auto-refresh in {nextRefreshIn}s
						</Text>
						<HStack>
							<Button leftIcon={<FaSyncAlt />} colorScheme="blue" onClick={handleRefresh} isDisabled={loading}>
								Refresh
							</Button>
						</HStack>
					</HStack>
				</HStack>
			</VStack>

			<Grid templateRows="1fr 1fr" gap={4}>
				{/* Running/Pending Builds Section */}
				<GridItem>
					<Box border="2px" borderColor={borderColor} borderRadius="5px" p={4} overflowY="auto" maxHeight="370px" minHeight="370px">
						<Text fontWeight="bold" mb={2}>
							Running/Pending Builds
							{selectedAppId !== 'all' && applications.length > 0 && (
								<Text as="span" fontWeight="normal" color="gray.500" ml={2}>
									({applications.find((app) => app._id === selectedAppId)?.appName})
								</Text>
							)}
						</Text>
						{loading ? (
							<Spinner />
						) : runningOrPendingBuilds.length > 0 ? (
							<BuildTable
								builds={runningOrPendingBuilds}
								onOpen={onOpen}
								onClose={onClose}
								isOpen={isOpen}
								setSelectedBuild={setSelectedBuild}
								isAdminOrDeveloper={isAdminOrDeveloper}
							/>
						) : (
							<Text>No running or pending builds.</Text>
						)}
					</Box>
				</GridItem>

				{/* Completed Builds Section */}
				<GridItem>
					<Box border="2px" borderColor={borderColor} borderRadius="5px" p={4} overflowY="auto" maxHeight="370px" minHeight="370px">
						<Text fontWeight="bold" mb={2}>
							Completed Builds
							{selectedAppId !== 'all' && applications.length > 0 && (
								<Text as="span" fontWeight="normal" color="gray.500" ml={2}>
									({applications.find((app) => app._id === selectedAppId)?.appName})
								</Text>
							)}
						</Text>
						{loading ? (
							<Spinner />
						) : completedBuilds.length > 0 ? (
							<BuildTable
								builds={completedBuilds}
								onOpen={onOpen}
								onClose={onClose}
								isOpen={isOpen}
								setSelectedBuild={setSelectedBuild}
								isAdminOrDeveloper={isAdminOrDeveloper}
							/>
						) : (
							<Text>No completed builds.</Text>
						)}
					</Box>
				</GridItem>
			</Grid>

			{/* Cancel Build Modal */}
			{selectedBuild && (
				<CancelBuildModal
					isOpen={isOpen}
					onClose={() => {
						setSelectedBuild(null); // Reset selected build
						onClose(); // Close the modal
					}}
					build={selectedBuild}
				/>
			)}
		</Box>
	);
};

// BuildTable component now not exported as it's used only within BuildsPage
const BuildTable = ({
	builds,
	onOpen,
	onClose,
	isOpen,
	setSelectedBuild,
	isAdminOrDeveloper,
}: {
	builds: CodemagicBuild[];
	onOpen: () => void;
	onClose: () => void;
	isOpen: boolean;
	setSelectedBuild: (build: CodemagicBuild | null) => void;
	isAdminOrDeveloper: boolean;
}) => {
	const getRowColor = (status: string) => {
		switch (status) {
			case 'finished':
				return 'green.700';
			case 'failed':
				return 'red.700';
			case 'canceled':
				return 'purple.700';
			case 'building':
				return 'blue.700';
			case 'queued':
				return 'yellow.700';
			case 'fetching':
			case 'preparing':
			case 'publishing':
			case 'finishing':
				return 'orange.500';
			default:
				return 'white';
		}
	};
	const handleCancelClick = (build: any) => {
		setSelectedBuild(build); // Set selected build
		onOpen(); // Open the cancel modal
	};

	const formatDate = (date: string | null) => {
		if (!date) return '-';
		return dayjs(date).format('HH:mm:ss DD-MM-YYYY');
	};

	const getPlatformIcon = (platform: string) => {
		const platformLower = platform?.toLowerCase();
		let icon, label;

		switch (platformLower) {
			case 'ios':
				icon = <FaApple size={16} color="#007AFF" />;
				label = 'iOS';
				break;
			case 'android':
				icon = <FaAndroid size={16} color="#3DDC84" />;
				label = 'Android';
				break;
			default:
				icon = <FaQuestion size={16} color="#6B7280" />;
				label = 'Unknown';
		}

		return (
			<Tooltip label={label} aria-label={`Platform: ${label}`}>
				{icon}
			</Tooltip>
		);
	};

	return (
		<TableContainer>
			<Table variant="simple" size="sm">
				<Thead sx={{ th: { textAlign: 'center', verticalAlign: 'middle' } }}>
					<Tr>
						<Th width="250px" maxW="200px" minW="200px">
							Name
						</Th>
						<Th width="80px" minW="60px">
							Platform
						</Th>
						<Th width="200px">Branch</Th>
						<Th width="100px">Version</Th>
						<Th width="100px">Build Number</Th>
						<Th width="100px">Status</Th>
						<Th width="200px">Start Time</Th>
						<Th width="200px">End Time</Th>
						<Th width="200px">Actions</Th>
					</Tr>
				</Thead>
				<Tbody>
					{builds.map((build) => (
						<Tr key={build._id} bg={getRowColor(build.status)}>
							<Td
								width="250px"
								maxW="250px"
								minW="250px"
								verticalAlign="middle"
								textAlign="left"
								whiteSpace="nowrap"
								overflow="hidden"
								textOverflow="ellipsis"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								<HStack spacing={2}>
									{build.appIconUrl && (
										<Box boxSize="40px" flexShrink={0}>
											<Image src={build.appIconUrl} alt="App Icon" objectFit="contain" borderRadius="md" />
										</Box>
									)}
									<Box
										as="span"
										textOverflow="ellipsis"
										whiteSpace="nowrap"
										overflow="hidden"
										maxWidth="250px"
										_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
									>
										{build.appName}
									</Box>
								</HStack>
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width="80px"
								maxW="80px"
								minW="60px"
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								<Box display="flex" justifyContent="center" alignItems="center">
									{getPlatformIcon(build.platform)}
								</Box>
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'200px'}
								maxW={'200px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{build.branch}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'100px'}
								maxW={'100px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{build.artefacts && build.artefacts.length > 0 ? build.artefacts[0].version : '-'}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'100px'}
								maxW={'100px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{build.artefacts && build.artefacts.length > 0 ? build.artefacts[0].versionCode : '-'}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'100px'}
								maxW={'100px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{build.status}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'200px'}
								maxW={'200px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{formatDate(build.startedAt)}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'200px'}
								maxW={'200px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{formatDate(build.finishedAt)}
							</Td>
							<Td
								textAlign="center"
								verticalAlign="middle"
								width={'200px'}
								maxW={'200px'}
								overflow="hidden"
								textOverflow="ellipsis"
								whiteSpace="nowrap"
								position="relative"
								_hover={{ overflow: 'visible', whiteSpace: 'normal' }}
							>
								{['building', 'queued', 'preparing'].includes(build.status) && (
									<Button colorScheme="red" size="xs" onClick={() => handleCancelClick(build)} isDisabled={!isAdminOrDeveloper}>
										Cancel
									</Button>
								)}
							</Td>
						</Tr>
					))}
				</Tbody>
			</Table>
		</TableContainer>
	);
};

export default BuildsPage;
